import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useWalletBalance } from '../../hooks/useWalletBalance';
import Tooltip from 'bootstrap/js/dist/tooltip';

interface TokenBalanceProps {
  email: string | null;
  token: string | null;
}

/**
 * TokenBalance component displays the user's token balance
 * with Bootstrap styling as a dropdown in the navbar.
 * Includes links to the wallet and transaction history.
 */
const TokenBalance: React.FC<TokenBalanceProps> = ({ email, token }) => {
  const { t } = useTranslation();
  const { balance, loading, isUsingCachedBalance } = useWalletBalance(email, token);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  
  // Initialize tooltip
  useEffect(() => {
    if (tooltipRef.current && isUsingCachedBalance) {
      const tooltip = new Tooltip(tooltipRef.current, {
        title: t('common.serverUnavailable', 'Server unavailable. Using cached balance.'),
        placement: 'bottom'
      });
      
      return () => {
        tooltip.dispose();
      };
    }
  }, [isUsingCachedBalance, t]);
  
  // If not logged in, don't render anything
  if (!email || !token) {
    return null;
  }
  
  return (
    <div className="nav-item dropdown">
      <button 
        className="nav-link dropdown-toggle px-3 d-flex align-items-center border-start border-end border-secondary text-decoration-none position-relative"
        id="tokenBalanceDropdown"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label={t('common.tokenBalance', 'Token Balance')}
      >
        <div className="d-flex align-items-center">
          <i className="bi bi-coin text-warning me-2 fs-5" aria-hidden="true"></i>
          {loading || isUsingCachedBalance ? (
            <span 
              className="spinner-border spinner-border-sm text-light" 
              role="status"
              ref={isUsingCachedBalance ? tooltipRef : undefined}
            >
              <span className="visually-hidden">{loading ? t('common.loading', 'Loading...') : t('common.serverUnavailable', 'Server unavailable')}</span>
            </span>
          ) : (
            <span className="text-light fw-bold">{balance}</span>
          )}
          <i className="bi bi-wallet2 text-light ms-2 small" aria-hidden="true"></i>
        </div>
        <span className="position-absolute bottom-0 start-50 translate-middle-x bg-primary" style={{height: "2px", width: "60%"}}></span>
      </button>
      
      <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="tokenBalanceDropdown">
        <li>
          <Link className="dropdown-item" to="/wallet">
            <i className="bi bi-wallet2 me-2"></i>
            {t('wallet.title', 'My Wallet')}
          </Link>
        </li>
        <li>
          <Link className="dropdown-item" to="/token-transactions">
            <i className="bi bi-receipt me-2"></i>
            {t('transactions.title', 'Transaction History')}
          </Link>
        </li>
      </ul>
    </div>
  );
};

export default TokenBalance; 